import React, { useEffect, useState, useCallback } from 'react';
import StaffRow from './StaffRow';
import './StaffTable.css';
import { axiosInstance } from '../config';

const StaffTable = ({ loading, setLoading, searchTerm, handleSearch, canPerformAction }) => {
  const [staffList, setStaffList] = useState([]);
  const [error, setError] = useState(null);
  const [filterBy, setFilterBy] = useState('all');
  const [sortColumn, setSortColumn] = useState('createdDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [exportLoading, setExportLoading] = useState(false);
  const userEnvironment = localStorage.getItem('userEnvironment') || 'test';
  
  const fetchStaff = useCallback(async () => {
    const token = localStorage.getItem('token');
    const businessId = localStorage.getItem('businessId');
    
    if (!token || !businessId) {
      console.log('No authentication credentials found');
      return;
    }
    
    if (!setLoading) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log('StaffTable - Fetch Request:', {
        userEnvironment,
        token,
        businessId
      });

      const response = await axiosInstance.get('/api/staff', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('StaffTable - Response:', {
        data: response.data,
        environment: userEnvironment
      });

      // Filter staff based on environment and archived status
      const filteredStaff = response.data.filter(staff => {
        // For live environment, only show live data
        if (userEnvironment === 'live') {
          return !staff.archived && (!staff.environment || staff.environment === 'live');
        }
        // For test environment, only show test data
        return !staff.archived && (!staff.environment || staff.environment === 'test');
      });

      setStaffList(filteredStaff);

    } catch (error) {
      console.error('Error fetching staff:', error.response?.data || error.message);
      setError(error.response?.data?.error || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, [setLoading, userEnvironment]);

  const handleStaffArchived = useCallback((staffId) => {
    setStaffList(currentStaff => currentStaff.filter(staff => staff.id !== staffId));
  }, []);

  const handleStaffUpdated = (updatedStaff) => {
    // Update the staff list with the updated staff member
    setStaffList(prevStaffList => 
      prevStaffList.map(staff => 
        staff.id === updatedStaff.id ? updatedStaff : staff
      )
    );
  };

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it as the sort column with ascending direction
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Helper function to format date for CSV export
  const formatDateForExport = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  // Function to export staff data as CSV
  const exportStaffData = async () => {
    try {
      setExportLoading(true);
      
      // Fetch all staff with their license checks
      const response = await axiosInstance.get('/api/staff/export');
      const rawData = response.data;
      
      if (!rawData || rawData.length === 0) {
        alert('No data available to export');
        setExportLoading(false);
        return;
      }
      
      // Define CSV headers
      const headers = [
        'Staff ID',
        'First Name',
        'Last Name',
        'License Number',
        'Created Date',
        'Check ID',
        'Check Date',
        'Common Entitlement Expiry',
        'Points',
        'Status',
        'License Valid To'
      ];
      
      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      
      // Process each row directly
      rawData.forEach(row => {
        const csvRow = [
          row.staff_id,
          escapeCsvValue(row.first_name || ''),
          escapeCsvValue(row.last_name || ''),
          escapeCsvValue(row.driving_license_number || ''),
          formatDateForExport(row.created_at),
          row.check_id || '',
          formatDateForExport(row.check_date),
          formatDateForExport(row.common_entitlement_expiry),
          row.points_total || 0,
          escapeCsvValue(row.status || ''),
          formatDateForExport(row.license_valid_to)
        ];
        
        csvContent += csvRow.join(',') + '\n';
      });
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename with current date
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0];
      link.download = `staff_export_${formattedDate}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportLoading(false);
    } catch (error) {
      console.error('Error exporting staff data:', error);
      alert('Failed to export staff data');
      setExportLoading(false);
    }
  };
  
  // Helper function to escape CSV values
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    
    // Convert to string
    const stringValue = String(value);
    
    // If the value contains commas, quotes, or newlines, wrap it in quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      // Double up any quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  };

  const getFilteredStaff = useCallback(() => {
    let filtered = staffList.filter(staff => {
      if (!staff || staff.archived) return false;
      
      // Additional environment check
      if (userEnvironment === 'live' && staff.environment === 'test') return false;
      if (userEnvironment === 'test' && staff.environment === 'live') return false;
      
      // Search term filter
      const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
      const licenseNumber = (staff.driving_license_number || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = fullName.includes(searchLower) || 
                           licenseNumber.includes(searchLower);

      if (!matchesSearch) return false;

      // Apply additional filters
      switch (filterBy) {
        case 'expiringSoon':
          if (!staff.license_valid_to) return false;
          const expiryDate = new Date(staff.license_valid_to);
          const thirtyDaysFromNow = new Date();
          thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
          return expiryDate <= thirtyDaysFromNow && expiryDate >= new Date();
          
        case 'expired':
          if (!staff.license_valid_to) return false;
          return new Date(staff.license_valid_to) < new Date();
          
        case 'highPoints':
          return (staff.points_total || 0) >= 6;
          
        default:
          return true;
      }
    });

    // Sort by expiry date if filtering by expiring soon
    if (filterBy === 'expiringSoon') {
      filtered.sort((a, b) => {
        if (!a.license_valid_to) return 1;
        if (!b.license_valid_to) return -1;
        return new Date(a.license_valid_to) - new Date(b.license_valid_to);
      });
    }

    // Apply sorting if a sort column is selected
    if (sortColumn) {
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        // Handle different column types for sorting
        switch (sortColumn) {
          case 'name':
            valueA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
            valueB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
            break;
          case 'licenseNumber':
            valueA = (a.driving_license_number || '').toLowerCase();
            valueB = (b.driving_license_number || '').toLowerCase();
            break;
          case 'status':
            valueA = (a.status || '').toLowerCase();
            valueB = (b.status || '').toLowerCase();
            break;
          case 'points':
            valueA = a.points_total || 0;
            valueB = b.points_total || 0;
            break;
          case 'lastCheckDate':
            valueA = a.last_checked ? new Date(a.last_checked) : new Date(0);
            valueB = b.last_checked ? new Date(b.last_checked) : new Date(0);
            break;
          case 'nextCheckDate':
            // For next check date, we need to calculate it based on last check date and frequency
            const business = JSON.parse(localStorage.getItem('business') || '{}');
            const emailFrequency = business.email_frequency || 'Daily';
            
            // Calculate next check dates for comparison
            valueA = calculateNextCheckDateForSorting(a.last_checked, emailFrequency);
            valueB = calculateNextCheckDateForSorting(b.last_checked, emailFrequency);
            break;
          case 'cardExpiry':
            valueA = a.license_valid_to ? new Date(a.license_valid_to) : new Date(0);
            valueB = b.license_valid_to ? new Date(b.license_valid_to) : new Date(0);
            break;
          case 'licenseValidUntil':
            // Using the common entitlement expiry from the staff object
            valueA = a.common_entitlement_expiry ? new Date(a.common_entitlement_expiry) : new Date(0);
            valueB = b.common_entitlement_expiry ? new Date(b.common_entitlement_expiry) : new Date(0);
            break;
          case 'age':
            valueA = calculateAge(a.date_of_birth);
            valueB = calculateAge(b.date_of_birth);
            break;
          default:
            return 0;
        }
        
        // Compare values based on sort direction
        if (valueA < valueB) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [staffList, searchTerm, filterBy, userEnvironment, sortColumn, sortDirection]);

  // Helper function to calculate next check date for sorting purposes
  const calculateNextCheckDateForSorting = (lastChecked, frequency) => {
    if (!lastChecked) return new Date(0); // If no last check date, put at the end when sorting
    
    const lastCheckDate = new Date(lastChecked);
    const now = new Date();
    let nextCheck = new Date(lastCheckDate);
    
    // Calculate next check based on frequency
    switch (frequency?.toLowerCase()) {
      case 'daily':
        nextCheck.setDate(nextCheck.getDate() + 1);
        break;
        
      case 'weekly':
        nextCheck.setDate(nextCheck.getDate() + 7);
        break;
        
      case 'monthly':
        nextCheck.setMonth(nextCheck.getMonth() + 1);
        break;
        
      case 'quarterly':
        nextCheck.setMonth(nextCheck.getMonth() + 3);
        break;
        
      case 'biannual':
      case 'semi-annual':
        nextCheck.setMonth(nextCheck.getMonth() + 6);
        break;
        
      case 'yearly':
      case 'annual':
        nextCheck.setFullYear(nextCheck.getFullYear() + 1);
        break;
        
      default:
        return new Date(0); // Default to earliest date for unknown frequency
    }

    // If the calculated next check is in the past, keep adding the interval until we get a future date
    while (nextCheck <= now) {
      switch (frequency?.toLowerCase()) {
        case 'daily':
          nextCheck.setDate(nextCheck.getDate() + 1);
          break;
        case 'weekly':
          nextCheck.setDate(nextCheck.getDate() + 7);
          break;
        case 'monthly':
          nextCheck.setMonth(nextCheck.getMonth() + 1);
          break;
        case 'quarterly':
          nextCheck.setMonth(nextCheck.getMonth() + 3);
          break;
        case 'biannual':
        case 'semi-annual':
          nextCheck.setMonth(nextCheck.getMonth() + 6);
          break;
        case 'yearly':
        case 'annual':
          nextCheck.setFullYear(nextCheck.getFullYear() + 1);
          break;
        default:
          return new Date(0);
      }
    }

    return nextCheck;
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 0;
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    
    // If birthday hasn't occurred yet this year, subtract one year
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const filteredStaff = getFilteredStaff();

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  // Helper function to render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return <span className="sort-indicator active">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
    }
    return <span className="sort-indicator inactive">▲▼</span>;
  };

  return (
    <div className="staff-table-container">
      <div className="search-section">
        <div className="search-controls">
          <input 
            type="text" 
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by name or license number..."
            className="table-search-input"
          />
          <select 
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Staff</option>
            <option value="expiringSoon">Expiring Soon (30 days)</option>
            <option value="expired">Expired Licenses</option>
            <option value="highPoints">High Points (6+)</option>
          </select>
          <button 
            onClick={exportStaffData} 
            className="export-btn"
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {!filteredStaff || filteredStaff.length === 0 ? (
        <div className="no-data">
          {userEnvironment === 'live' 
            ? "No live staff records found"
            : "No test staff records found"}
        </div>
      ) : (
        <table className="staff-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable-header">
                Name {renderSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('licenseNumber')} className="sortable-header">
                License Number {renderSortIndicator('licenseNumber')}
              </th>
              <th onClick={() => handleSort('age')} className="sortable-header age-cell">
                Age {renderSortIndicator('age')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable-header">
                Status {renderSortIndicator('status')}
              </th>
              <th onClick={() => handleSort('points')} className="sortable-header points-cell">
                Points {renderSortIndicator('points')}
              </th>
              <th onClick={() => handleSort('lastCheckDate')} className="sortable-header">
                Last Check Date {renderSortIndicator('lastCheckDate')}
              </th>
              <th onClick={() => handleSort('nextCheckDate')} className="sortable-header">
                Next Check Date {renderSortIndicator('nextCheckDate')}
              </th>
              <th onClick={() => handleSort('cardExpiry')} className="sortable-header">
                Card Expiry {renderSortIndicator('cardExpiry')}
              </th>
              <th onClick={() => handleSort('licenseValidUntil')} className="sortable-header">
                License Valid Until {renderSortIndicator('licenseValidUntil')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.map(staff => (
              <StaffRow 
                key={staff.id} 
                staff={staff} 
                onRefresh={fetchStaff}
                onArchived={() => handleStaffArchived(staff.id)}
                onStaffUpdated={handleStaffUpdated}
                canPerformAction={canPerformAction}
                userEnvironment={userEnvironment}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default StaffTable; 